import React from 'react';
import { Modal } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import EditMerchantModal from 'Components/MerchantOverviewModal/EditModal';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import { strParamReplacer } from 'Utils';
import useUpdateMerchantOverview from 'Hooks/query-hooks/single-merchant/useUpdateMerchantOverview';

type Props = Pick<React.ComponentProps<typeof Modal>, 'isOpen' | 'closeModal'> & {
    globalThresholdText?: string;
    globalThreshold: number;
    merchantRules: MerchantRulesData;
    templateRepositoryId: number;
    merchantId: string;
};

function EditMercahntOverviewModal({
    isOpen,
    closeModal,
    globalThresholdText,
    globalThreshold,
    merchantRules,
    templateRepositoryId,
    merchantId
}: Props) {
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const thresholdConfig = merchantRules.config;
    const overallThreshold = Number(thresholdConfig?.parameters?.overallThreshold);
    const inheritParameters = String(merchantRules?.config?.inheritParameters);
    const merchantName = merchantRules?.config?.merchantName;
    const merchantDescription = merchantRules?.config?.merchantDescription;
    const mutation = useUpdateMerchantOverview({
        repoId: templateRepositoryId,
        revision: merchantRules.revision,
        merchantId,
        mutationOptions: {
            onSuccess: () => {
                closeModal();
                addAlert({
                    id: 'merchant-overview-run',
                    status: 'success',
                    content: t('Merchant Overview successfully updated')
                });
            },
            onError: () => {
                closeModal();
            }
        },
        overrideLevel: 'merchant'
    });
    const thresholdText = strParamReplacer(thresholdConfig?.inputDescription, { type: 'emptyString' });
    const initialValues = {
        merchantName,
        merchantDescription,
        overallThreshold: inheritParameters === 'MERCHANT' ? overallThreshold : undefined
    };
    const handleSubmit = async (formData: Omit<MerchantFormState, 'id'>) => {
        try {
            const data = {
                templateRepositoryId,
                merchantId,
                config: {
                    parameters: formData.overallThreshold
                        ? {
                              overallThreshold: _.toNumber(formData.overallThreshold)
                          }
                        : null,
                    merchantDescription: formData?.merchantDescription,
                    merchantName: formData?.merchantName
                }
            };
            mutation.mutate(data);
        } catch (_error) {
            // eslint-disable-next-line no-console
            console.error(_error);
        }
    };

    return isOpen ? (
        <EditMerchantModal
            initialValueProp={initialValues}
            inheritParameters={inheritParameters}
            overallThreshold={overallThreshold}
            globalThreshold={globalThreshold}
            globalThresholdText={String(globalThresholdText)}
            merchantThresholdText={String(thresholdText)}
            handleSubmit={handleSubmit}
            isOpen={isOpen}
            closeModal={closeModal}
            isLoading={mutation.isLoading}
        />
    ) : null;
}

export default EditMercahntOverviewModal;
